// Schema version for data migrations
export const SCHEMA_VERSION = 1;

// Unique ID type
export type UUID = string;

// Time in seconds
export type Seconds = number;

// Unit types for ingredients
export type IngredientUnit =
  | 'g' | 'kg' | 'mg'
  | 'ml' | 'l' | 'dl'
  | 'cup' | 'tbsp' | 'tsp'
  | 'oz' | 'lb'
  | 'piece' | 'whole'
  | 'pinch' | 'to taste'
  | 'clove' | 'slice' | 'bunch'
  | '';

// Difficulty levels
export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';

// Cuisine types
export type Cuisine =
  | 'italian' | 'mexican' | 'chinese' | 'japanese' | 'indian'
  | 'french' | 'thai' | 'mediterranean' | 'american' | 'korean'
  | 'vietnamese' | 'greek' | 'spanish' | 'middle-eastern' | 'other';

// Dietary tags
export type DietaryTag =
  | 'vegetarian' | 'vegan' | 'gluten-free' | 'dairy-free'
  | 'nut-free' | 'low-carb' | 'keto' | 'paleo'
  | 'pescatarian' | 'halal' | 'kosher';

// Ingredient definition
export interface Ingredient {
  id: UUID;
  name: string;
  amount: number;
  unit: IngredientUnit;
  notes?: string;
  optional?: boolean;
  group?: string; // For grouping ingredients (e.g., "For the sauce")
}

// Recipe step with optional timer
export interface RecipeStep {
  id: UUID;
  order: number;
  instruction: string;
  phase?: 'prep' | 'cook'; // Optional authoring phase for grouped editing UI
  timerDuration?: Seconds; // Optional timer duration for this step
  timerLabel?: string; // Label for auto-created timer
  notes?: string;
  imageUrl?: string;
}

// Full recipe definition
export interface Recipe {
  id: UUID;
  name: string;
  description: string;
  imageUrl?: string;
  
  // Timing
  prepTime: Seconds;
  cookTime: Seconds;
  totalTime: Seconds;
  
  // Servings
  servings: number;
  
  // Difficulty and categorization
  difficulty: Difficulty;
  cuisine: Cuisine;
  tags: string[];
  dietaryTags: DietaryTag[];
  
  // Content
  ingredients: Ingredient[];
  steps: RecipeStep[];
  notes?: string;
  source?: string; // URL or attribution
  createdBy?: string; // User UID or 'recipesense' for community
  isPublic?: boolean; // true = discoverable, false = private (default)

  // Metadata
  isFavorite: boolean;
  createdAt: string; // ISO date string
  updatedAt: string;
  lastCookedAt?: string;
  cookCount: number;
  
  // For import/export compatibility
  schemaVersion: number;
}

// Meal planning
export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface MealPlanEntry {
  id: UUID;
  date: string; // YYYY-MM-DD (local calendar date)
  mealType: MealType;
  recipeId?: UUID; // Optional — absent for custom/free-text meals
  customMealName?: string; // Free-text meal name (e.g. "Leftovers", "Eating out")
  servings: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Timer state for cooking mode
export type TimerStatus = 'idle' | 'running' | 'paused' | 'completed';

export interface Timer {
  id: UUID;
  label: string;
  duration: Seconds;
  remaining: Seconds;
  status: TimerStatus;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  linkedStepId?: UUID; // If timer is linked to a recipe step
}

// Cooking session state
export interface CookingSession {
  id: UUID;
  recipeId?: UUID; // If cooking from a recipe
  recipeName?: string;
  startedAt: string;
  currentStepIndex: number;
  completedStepIds: UUID[];
  checkedIngredientIds: UUID[];
  timers: Timer[];
  notes: string;
  checklistItems: ChecklistItem[];
  servingMultiplier: number;
}

// Checklist item for cooking mode
export interface ChecklistItem {
  id: UUID;
  text: string;
  checked: boolean;
  createdAt: string;
}

// Shopping list item
export interface ShoppingListItem {
  id: UUID;
  ingredientName: string;
  amount: number;
  unit: IngredientUnit;
  recipeId: UUID;
  recipeName: string;
  checked: boolean;
}

// Pantry
export type PantryCategory =
  | 'produce'
  | 'protein'
  | 'dairy'
  | 'grains'
  | 'canned'
  | 'spices'
  | 'condiments'
  | 'snacks'
  | 'beverages'
  | 'frozen'
  | 'baking'
  | 'other';

export type PantryLocation = 'pantry' | 'fridge' | 'freezer' | 'counter' | 'other';

export interface PantryItem {
  id: UUID;
  ingredientName: string;
  amount: number;
  unit: IngredientUnit;
  category: PantryCategory;
  location: PantryLocation;
  lowStockThreshold: number;
  expiresAt?: string; // ISO date
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Cooking history entry
export interface CookingHistoryEntry {
  id: UUID;
  recipeId: UUID;
  recipeName: string;
  cookedAt: string;
  notes?: string;
  rating?: number; // 1-5
  duration?: Seconds; // Actual time spent
}

// App settings
export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  reducedMotion: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  defaultServings: number;
  preferredUnits: 'metric' | 'imperial';
  timerAlertVolume: number; // 0-100
  keepScreenAwake: boolean;
  autoStartTimers: boolean;
  hasSeenCookbookWelcome?: boolean;
  /** Versioned-migration marker for settings defaults; see migrateSettings() in lib/db.ts. */
  settingsVersion?: number;
}

// Full app state for export/import
export interface CookbookExport {
  schemaVersion: number;
  exportedAt: string;
  recipes: Recipe[];
  cookingHistory: CookingHistoryEntry[];
  shoppingList: ShoppingListItem[];
  pantryItems?: PantryItem[];
  mealPlans?: MealPlanEntry[];
  settings: AppSettings;
}

// Filter state for cookbook
export interface CookbookFilters {
  search: string;
  cuisine: Cuisine | 'all';
  difficulty: Difficulty | 'all';
  dietaryTags: DietaryTag[];
  favoritesOnly: boolean;
  sortBy: 'name' | 'createdAt' | 'updatedAt' | 'lastCookedAt' | 'cookCount';
  sortOrder: 'asc' | 'desc';
}

// Random recipe API response (for external API)
export interface ExternalRecipeResponse {
  id: string;
  title: string;
  image: string;
  summary: string;
  readyInMinutes: number;
  servings: number;
  sourceUrl?: string;
  extendedIngredients?: Array<{
    id: number;
    original: string;
    name: string;
    amount: number;
    unit: string;
  }>;
  analyzedInstructions?: Array<{
    steps: Array<{
      number: number;
      step: string;
      length?: { number: number; unit: string };
    }>;
  }>;
}

// Utility types
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// Create recipe input (for forms)
export type RecipeInput = Omit<Recipe, 'id' | 'createdAt' | 'updatedAt' | 'cookCount' | 'schemaVersion'>;

// Unit conversion rates (to base unit in category)
export const UNIT_CONVERSIONS: Record<string, { base: string; rate: number }> = {
  // Weight (base: g)
  g: { base: 'g', rate: 1 },
  kg: { base: 'g', rate: 1000 },
  mg: { base: 'g', rate: 0.001 },
  oz: { base: 'g', rate: 28.3495 },
  lb: { base: 'g', rate: 453.592 },
  
  // Volume (base: ml)
  ml: { base: 'ml', rate: 1 },
  l: { base: 'ml', rate: 1000 },
  dl: { base: 'ml', rate: 100 },
  cup: { base: 'ml', rate: 236.588 },
  tbsp: { base: 'ml', rate: 14.787 },
  tsp: { base: 'ml', rate: 4.929 },
};
